import { useEffect, useMemo, useState } from 'react';
import type { AnalyticsSummary, KeywordRow } from '@zen-bell/shared';
import { adminFetch } from '../lib/api';
import { LineChart, type Dataset } from './LineChart';
import { RangePicker, lastDays, percent, query, type DateRange } from './RangePicker';

/** Dashboard section for issue #4: post views, download clicks and searches. */
export function ContentAnalytics() {
  const [range, setRange] = useState<DateRange>(() => lastDays(30));
  const [data, setData] = useState<AnalyticsSummary | null>(null);
  const [error, setError] = useState(false);
  // Top Posts by reach or by the downloads they drive.
  const [topBy, setTopBy] = useState<'views' | 'cta'>('views');

  useEffect(() => {
    let live = true;
    setError(false);
    adminFetch<AnalyticsSummary>(`/analytics/summary?${query(range)}`)
      .then((d) => live && setData(d))
      .catch(() => live && setError(true));
    return () => {
      live = false;
    };
  }, [range]);

  const chart = useMemo(() => {
    const daily = data?.daily ?? [];
    const datasets: Dataset[] = [
      { label: 'Post Views', data: daily.map((d) => d.views), borderColor: '#ffffff', tension: 0.3 },
      { label: 'Download Clicks', data: daily.map((d) => d.cta), borderColor: '#c9a84c', tension: 0.3 },
    ];
    return { labels: daily.map((d) => d.date), datasets };
  }, [data]);

  const t = data?.totals;
  const topPosts = (data?.posts ?? [])
    .filter((p) => p.views || p.cta)
    .sort((a, b) => b[topBy] - a[topBy] || b.views - a.views)
    .slice(0, 10);

  return (
    <section id="content-analytics">
      <div className="header">
        <h2>Content Analytics</h2>
        <div>
          <RangePicker value={range} onChange={setRange} />
        </div>
      </div>

      {error ? <p className="empty-state">Failed to load content analytics.</p> : null}
      {!data && !error ? <div className="loading">Fetching data...</div> : null}

      {data && t ? (
        <>
          <div className="stats-grid">
            <Kpi label="Post Views" value={t.views} hint={`${t.visitors.toLocaleString()} unique visitors`} />
            <Kpi label="Download Clicks" value={t.cta} hint={`App Store ${t.app_store} · Google Play ${t.google_play} · Download ${t.download}`} />
            <Kpi label="Conversion Rate" value={percent(t.conversion)} hint="download clicks / post views" />
            <Kpi label="Searches" value={t.searches} hint={`${data.noResultKeywords.length} keywords without results`} />
          </div>

          <LineChart labels={chart.labels} datasets={chart.datasets} />

          <div className="section-head">
            <h3 className="section-title">Top Posts</h3>
            <select
              className="filter-select"
              value={topBy}
              aria-label="Rank top posts by"
              onChange={(e) => setTopBy(e.target.value as 'views' | 'cta')}
            >
              <option value="views">Most viewed</option>
              <option value="cta">Most downloads driven</option>
            </select>
          </div>
          <div className="panel table-panel">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Post</th>
                  <th className="num">Views</th>
                  <th className="num">Visitors</th>
                  <th className="num">App Store</th>
                  <th className="num">Google Play</th>
                  <th className="num">Download</th>
                  <th className="num">Total Clicks</th>
                  <th className="num">Conversion</th>
                </tr>
              </thead>
              <tbody>
                {topPosts.length === 0 ? (
                  <tr><td colSpan={8} className="empty-state">No post activity in this range.</td></tr>
                ) : (
                  topPosts.map((p) => (
                    <tr key={p.postId}>
                      <td>{p.title}</td>
                      <td className="num">{p.views}</td>
                      <td className="num">{p.visitors}</td>
                      <td className="num">{p.app_store}</td>
                      <td className="num">{p.google_play}</td>
                      <td className="num">{p.download}</td>
                      <td className="num">{p.cta}</td>
                      <td className="num">{percent(p.conversion)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="analytics-columns">
            <KeywordTable title="Top Keywords" rows={data.keywords} withClicks empty="No searches in this range." />
            <KeywordTable title="Keywords Without Results" rows={data.noResultKeywords} empty="Every search found something." />
            <div>
              <h3 className="section-title">Search → Post</h3>
              <div className="panel table-panel">
                <table className="data-table">
                  <thead><tr><th>Keyword</th><th>Post Opened</th><th className="num">Clicks</th></tr></thead>
                  <tbody>
                    {data.keywordPosts.length === 0 ? (
                      <tr><td colSpan={3} className="empty-state">No search result was opened in this range.</td></tr>
                    ) : (
                      data.keywordPosts.map((r) => (
                        <tr key={`${r.keyword}|${r.postId}`}>
                          <td>{r.keyword}</td>
                          <td>{r.title}</td>
                          <td className="num">{r.clicks}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div>
              <h3 className="section-title">Traffic Sources</h3>
              <div className="panel table-panel">
                <table className="data-table">
                  <thead><tr><th>Source</th><th className="num">Views</th></tr></thead>
                  <tbody>
                    {data.sources.length === 0 ? (
                      <tr><td colSpan={2} className="empty-state">No post views in this range.</td></tr>
                    ) : (
                      data.sources.map((s) => (
                        <tr key={s.source}><td>{s.source}</td><td className="num">{s.views}</td></tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </section>
  );
}

export function Kpi({ label, value, hint }: { label: string; value: number | string; hint?: string }) {
  return (
    <div className="stat-card">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{typeof value === 'number' ? value.toLocaleString() : value}</div>
      {hint ? <div className="stat-hint">{hint}</div> : null}
    </div>
  );
}

export function KeywordTable({
  title,
  rows,
  withClicks = false,
  empty,
}: {
  title: string;
  rows: KeywordRow[];
  withClicks?: boolean;
  empty: string;
}) {
  return (
    <div>
      <h3 className="section-title">{title}</h3>
      <div className="panel table-panel">
        <table className="data-table">
          <thead>
            <tr>
              <th>Keyword</th>
              {rows.some((r) => r.searches) || !withClicks ? <th className="num">Searches</th> : null}
              {withClicks ? <th className="num">Result Clicks</th> : null}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={3} className="empty-state">{empty}</td></tr>
            ) : (
              rows.map((r) => (
                <tr key={r.keyword}>
                  <td>{r.keyword}</td>
                  {rows.some((x) => x.searches) || !withClicks ? <td className="num">{r.searches}</td> : null}
                  {withClicks ? <td className="num">{r.clicks}</td> : null}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
